import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { FaDownload } from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';

export default function AdminOrders() {
  const { t } = useTranslation();
  const { API } = useContext(AppContext);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/orders/${orderId}?status=${status}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Error updating order');
    }
  };

  const downloadInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/orders/${orderId}/invoice`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Invoice downloaded');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Error downloading invoice');
    }
  };

  return (
    <AdminLayout>
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-orders">
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-4xl font-bold mb-8 text-[#0A2540]">{t('admin.orders')}</h1>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F5F5F5]">
                  <tr>
                    <th className="p-4 text-left">Order ID</th>
                    <th className="p-4 text-left">Customer</th>
                    <th className="p-4 text-left">Total</th>
                    <th className="p-4 text-left">Payment</th>
                    <th className="p-4 text-left">Status</th>
                    <th className="p-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr key={order.id} className="border-t" data-testid={`order-row-${index}`}>
                      <td className="p-4 text-sm font-mono">{order.id.substring(0, 8)}</td>
                      <td className="p-4">
                        <div className="font-semibold">{order.user_name}</div>
                        <div className="text-sm text-gray-600">{order.user_email}</div>
                      </td>
                      <td className="p-4 font-bold text-[#0A2540]">${order.total.toFixed(2)}</td>
                      <td className="p-4 text-sm capitalize">{order.payment_method.replace('_', ' ')}</td>
                      <td className="p-4">
                        <Select value={order.status} onValueChange={(value) => updateStatus(order.id, value)}>
                          <SelectTrigger className="w-32" data-testid={`order-status-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="confirmed">Confirmed</SelectItem>
                            <SelectItem value="shipped">Shipped</SelectItem>
                            <SelectItem value="delivered">Delivered</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <Button size="sm" variant="outline" onClick={() => downloadInvoice(order.id)} data-testid={`download-invoice-${index}`}>
                          <FaDownload className="mr-2" /> Invoice
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
    </AdminLayout>
  );
}